import { useState, useMemo, useRef, useEffect } from 'react'
import { useTheme } from '../context/ThemeContext'

/**
 * Well Review lease picker: optional Route filter -> Lease (names only, grouped by
 * route, fully scrollable) -> optional Well drill-down after a lease is chosen.
 *
 * @param {Array}  combos         selector hierarchy from /api/well-analysis ({comboCurveTie, route, wells[]})
 * @param {string} selectedCombo  currently selected lease (ComboCurveTie)
 * @param {string} selectedWell   currently selected well (WolfpakWellName) or '' for whole lease
 * @param {Function} onSelectLease (combo) => void
 * @param {Function} onSelectWell  (well|null) => void
 */
function LeaseRouteSelector({ combos = [], selectedCombo, selectedWell, onSelectLease, onSelectWell }) {
    const { theme } = useTheme()
    const isDark = theme === 'dark'
    const [route, setRoute] = useState('__all__')
    const [search, setSearch] = useState('')
    const [open, setOpen] = useState(false)
    const boxRef = useRef(null)

    useEffect(() => {
        const onDoc = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false) }
        document.addEventListener('mousedown', onDoc)
        return () => document.removeEventListener('mousedown', onDoc)
    }, [])

    // Distinct routes (Unassigned last)
    const routes = useMemo(() => {
        const set = new Set(combos.map(c => c.route || 'Unassigned'))
        return [...set].sort((a, b) =>
            a === 'Unassigned' ? 1 : b === 'Unassigned' ? -1 : a.localeCompare(b))
    }, [combos])

    // Leases filtered by route + search, grouped by route, sorted by name
    const grouped = useMemo(() => {
        const q = search.toLowerCase().trim()
        let list = combos
        if (route !== '__all__') list = list.filter(c => (c.route || 'Unassigned') === route)
        if (q) list = list.filter(c => (c.comboCurveTie || '').toLowerCase().includes(q))
        const byRoute = {}
        for (const c of list) {
            const r = c.route || 'Unassigned'
            ;(byRoute[r] = byRoute[r] || []).push(c)
        }
        const orderedRoutes = Object.keys(byRoute).sort((a, b) =>
            a === 'Unassigned' ? 1 : b === 'Unassigned' ? -1 : a.localeCompare(b))
        return orderedRoutes.map(r => ({
            route: r,
            leases: byRoute[r].sort((a, b) => (a.comboCurveTie || '').localeCompare(b.comboCurveTie || ''))
        }))
    }, [combos, route, search])

    const totalShown = grouped.reduce((n, g) => n + g.leases.length, 0)
    const selectedObj = combos.find(c => c.comboCurveTie === selectedCombo)
    const wells = selectedObj?.wells || []

    const pickLease = (combo) => {
        onSelectLease(combo)
        setSearch('')
        setOpen(false)
    }

    const selectCls = `w-full px-3 py-2 text-sm rounded-lg border-2 outline-none transition-colors ${
        isDark ? 'bg-slate-800 border-slate-600 text-gray-100 focus:border-blue-500'
               : 'bg-white border-gray-300 text-gray-900 focus:border-blue-500'}`

    return (
        <div className="w-full sm:w-80 space-y-2">
            {/* Route filter */}
            <label className={`block text-[10px] font-semibold uppercase tracking-wide ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                Route
            </label>
            <select value={route} onChange={e => setRoute(e.target.value)} className={selectCls}>
                <option value="__all__">All routes ({combos.length} leases)</option>
                {routes.map(r => (
                    <option key={r} value={r}>{r}</option>
                ))}
            </select>

            {/* Lease searchable dropdown (names only, grouped, scrollable) */}
            <div className="relative" ref={boxRef}>
                <input
                    type="text"
                    value={open ? search : (selectedCombo || search)}
                    onChange={e => { setSearch(e.target.value); setOpen(true) }}
                    onFocus={() => setOpen(true)}
                    placeholder={selectedCombo ? selectedCombo : 'Search or pick a lease…'}
                    className={selectCls}
                />
                {open && (
                    <div className={`absolute z-30 mt-1 w-full max-h-80 overflow-y-auto rounded-lg border-2 shadow-lg ${
                        isDark ? 'bg-slate-900 border-slate-700' : 'bg-white border-gray-300'}`}>
                        {totalShown === 0 && (
                            <div className={`px-3 py-2 text-sm ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>No leases match</div>
                        )}
                        {grouped.map(g => (
                            <div key={g.route}>
                                {route === '__all__' && (
                                    <div className={`sticky top-0 px-3 py-1 text-[10px] font-bold uppercase tracking-wide ${
                                        isDark ? 'bg-slate-800 text-blue-300' : 'bg-gray-100 text-blue-700'}`}>
                                        {g.route}
                                    </div>
                                )}
                                {g.leases.map(c => (
                                    <button key={c.comboCurveTie}
                                        onClick={() => pickLease(c.comboCurveTie)}
                                        className={`w-full text-left px-3 py-2 text-sm transition-colors ${
                                            c.comboCurveTie === selectedCombo
                                                ? (isDark ? 'bg-blue-900/40 text-blue-200' : 'bg-blue-50 text-blue-700')
                                                : (isDark ? 'text-gray-200 hover:bg-slate-800' : 'text-gray-700 hover:bg-gray-100')}`}>
                                        {c.comboCurveTie}
                                        <span className={`ml-2 text-[10px] ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                                            {(c.wells || []).length} well{(c.wells || []).length === 1 ? '' : 's'}
                                        </span>
                                    </button>
                                ))}
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* Well drill-down (after a lease is picked) */}
            {selectedCombo && wells.length > 0 && (
                <>
                    <label className={`block text-[10px] font-semibold uppercase tracking-wide ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                        Well
                    </label>
                    <select value={selectedWell || ''} onChange={e => onSelectWell(e.target.value || null)} className={selectCls}>
                        <option value="">Whole lease ({wells.length} wells)</option>
                        {wells
                            .slice()
                            .sort((a, b) => (a.wolfpakWellName || '').localeCompare(b.wolfpakWellName || ''))
                            .map(w => (
                                <option key={w.wolfpakWellName} value={w.wolfpakWellName}>{w.wolfpakWellName}</option>
                            ))}
                    </select>
                </>
            )}
        </div>
    )
}

export default LeaseRouteSelector
